// external libraries
import { addWeeks, endOfDay, startOfDay, subDays } from 'date-fns';

// types
import type { Period } from 'components/shared/DateNavigation/DateNavigation.types';
import type { Interval } from 'components/types';

// utils
import { getMonthInterval, getWeekInterval } from 'utils/date';

export type GetPeriodBoundsProps = {
  period: Period | null;
  selectedDate: Date | null;
};

/**
 * The interval the four roster queries fetch, derived from the shared date navigation state.
 *
 * Two oddities are existing behaviour and reproduced on purpose (see the pitfalls table in
 * `plans/2481-rosterplan-structuur/context.md`):
 *
 * - `fourWeeks` is asymmetric: the start snaps to the week, the end does not — it is four weeks
 *   minus a day from the selected date itself. With a selected date mid-week the range is
 *   therefore not 28 days. `getFourWeekInterval` in `utils/date.ts` is the symmetric version
 *   (week start through the end of the fourth week) and is deliberately *not* reused here — it
 *   moves the fetched end date by up to two days. Note before "fixing" that: the fourWeeks label
 *   in the date navigation toolbar is built from that same helper
 *   (`DateNavigation.utils.ts:74`), so the label and the fetched interval already disagree for a
 *   mid-week selected date. Reconciling them changes behaviour, so it is #2665 rather than part of
 *   this refactor.
 * - `custom` falls into the day default, so the queries fetch a single day while the grid still
 *   shows every column of `selectedDateRange`.
 *
 * A missing selected date falls back to today, the same fallback the shared interval helpers in
 * `utils/date.ts` make; the ternaries this replaces produced an invalid date and threw on
 * `toISOString()`.
 */
export const getPeriodBounds = ({
  period,
  selectedDate
}: GetPeriodBoundsProps): Interval => {
  const date = selectedDate ?? new Date();

  switch (period) {
    case 'week':
      return getWeekInterval(date);
    case 'month':
      return getMonthInterval(date);
    case 'fourWeeks':
      return {
        startDate: getWeekInterval(date).startDate,
        endDate: subDays(addWeeks(date, 4), 1)
      };
    default:
      return { startDate: startOfDay(date), endDate: endOfDay(date) };
  }
};
